import { ISignedWaiver } from "@/interfaces/waivers";
import { requestServices } from "@/utils/request";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import { EngineKeys } from "@/app-search/services/request-app-search.service";
import {
  getHttpStatus,
  getServicesErrorReason,
  withNotSyncedRetry,
} from "@/utils/helpers/not-synced-retry";

const WAIVER_INDEX = import.meta.env.VITE_ELASTIC_WAIVERS;
export const WAIVER_PAGE_SIZE = 10;

// In mapHitToWaiver:
function mapHitToWaiver(source: Record<string, any>): ISignedWaiver {
  return {
    waiverId: String(source.waiver_id),
    customerName: source.waiver_signatory_full_name || "",
    signedAt: source.waiver_created_at || "",
    waiverTitle: source.waiver_template_title || "",
    numberOfDependents: source.waiver_dependents_count ?? null,
  };
}

/**
 * Fetch signed waivers for the group from Elasticsearch.
 * Supports optional search query on waiver_signatory_full_name, waiver_id,
 * and waiver_template_title. Supports pagination via `from`.
 */
export async function getSignedWaivers(
  query?: string,
  from = 0
): Promise<{ waivers: ISignedWaiver[]; total: number }> {
  const groupId = POSConfigModule.groupId;
  const token = UserModule?.getUser?.elasticSearchTokens?.[EngineKeys.Waiver];

  const esPayload = query
    ? {
        query: {
          bool: {
            must: [
              {
                query_string: {
                  query: `*${query}*`,
                  fields: [
                    "waiver_signatory_full_name",
                    "waiver_template_title",
                  ],
                  default_operator: "AND",
                },
              },
            ],
            filter: [{ term: { group_access: groupId } }],
          },
        },
        from,
        size: WAIVER_PAGE_SIZE,
      }
    : {
        query: {
          bool: {
            filter: [{ term: { group_access: groupId } }],
          },
        },
        from,
        size: WAIVER_PAGE_SIZE,
      };

  const response = await requestServices({
    method: "POST",
    url: `/v1/search/${WAIVER_INDEX}`,
    data: esPayload,
    headers: { Authorization: `Bearer ${token}` },
  });

  const hits: Record<string, any>[] = response.data?.data?.hits?.hits || [];
  const total: number = response.data?.data?.hits?.total?.value || 0;

  return {
    waivers: hits.map((hit) => mapHitToWaiver(hit._source)),
    total,
  };
}

/** Reason the services API puts in `error.details.reason` on the order-keyed waiver endpoints. */
export enum WaiverErrorReason {
  /** 409: the order is not synced to the legacy booking table yet. */
  OrderNotSynced = "ORDER_NOT_SYNCED",
  /** 403 on link: the waiver belongs to another group than the order. */
  GroupMismatch = "GROUP_MISMATCH",
}

function isOrderNotSynced(err: unknown): boolean {
  return (
    getHttpStatus(err) === 409 &&
    getServicesErrorReason(err) === WaiverErrorReason.OrderNotSynced
  );
}

interface IOrderLinkedWaiversResponse {
  orderId: string;
  /** Equals signedWaiverIds.length: every link row of the order, no expiry filter. */
  count: number;
  signedWaiverIds: number[];
}

/**
 * Signed waivers linked to a NOL order.
 * `GET /signed-waivers/orders/:orderId`
 *
 * Retries while the order is not synced yet (409 ORDER_NOT_SYNCED), then
 * throws the last error.
 *
 * @param orderId Order id
 * @param retries Attempts after the first one, 3 s apart. The completed-step
 * count runs behind an inline spinner, so it defaults to about 30 s, longer
 * than a cashier-triggered link or unlink.
 * @param isStillNeeded Checked before each retry; the aside passes its
 * disposed flag so an unmounted scope stops polling instead of running out
 * its budget.
 * @returns the linked signed waiver ids, as strings to match ISignedWaiver.waiverId
 */
export async function getOrderLinkedWaiverIds(
  orderId: string,
  retries = 10,
  isStillNeeded: () => boolean = () => true
): Promise<string[]> {
  const response = await withNotSyncedRetry(
    () =>
      requestServices({
        method: "GET",
        url: `/signed-waivers/orders/${orderId}`,
      }),
    (err) => isOrderNotSynced(err) && isStillNeeded(),
    retries
  );
  const { signedWaiverIds }: IOrderLinkedWaiversResponse = response.data.data;
  return signedWaiverIds.map(String);
}

/**
 * Fetch active waiver template version id for the group.
 * GET /waivers?groupId={groupId}
 * Returns the activeVersion.id of the template with status: 1
 */
export async function getActiveWaiverTemplateVersionId(): Promise<
  number | null
> {
  const groupId = POSConfigModule.groupId;
  const response = await requestServices({
    method: "GET",
    url: `/waivers`,
    params: { groupId },
  });

  const templates: Record<string, any>[] = response.data?.data || [];
  const activeTemplate = templates.find((t) => t.status === 1);
  return activeTemplate?.activeVersion?.id ?? null;
}

/**
 * Link a signed waiver to a NOL order. Idempotent on relink.
 * `POST /signed-waivers/:id/orders`
 *
 * Retries while the order is not synced yet (409 ORDER_NOT_SYNCED), then
 * throws the last error. A waiver of another group is refused with 403
 * GROUP_MISMATCH, not retried.
 *
 * @param waiverId Signed waiver id
 * @param orderId Order id
 */
export async function linkWaiverToOrder(
  waiverId: string,
  orderId: string
): Promise<void> {
  await withNotSyncedRetry(
    () =>
      requestServices({
        method: "POST",
        url: `/signed-waivers/${waiverId}/orders`,
        data: { orderId },
      }),
    isOrderNotSynced
  );
}

/**
 * Unlink a signed waiver from a NOL order. Idempotent: answers 200 with
 * `removed: false` when the pair was not linked.
 * `DELETE /signed-waivers/:id/orders/:orderId`
 *
 * Retries while the order is not synced yet (409 ORDER_NOT_SYNCED), then
 * throws the last error.
 *
 * @param waiverId Signed waiver id
 * @param orderId Order id
 */
export async function unlinkWaiverFromOrder(
  waiverId: string,
  orderId: string
): Promise<void> {
  await withNotSyncedRetry(
    () =>
      requestServices({
        method: "DELETE",
        url: `/signed-waivers/${waiverId}/orders/${orderId}`,
      }),
    isOrderNotSynced
  );
}
